import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { axiosInstance, API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Settings, Users, MapPin, Package, LogOut, Plus, Pencil, Trash2, Key, AlertCircle, CheckCircle, XCircle, FileText, Ban, BarChart3, Download, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";

export default function AdminDashboard() {
  const { i18n } = useTranslation();
  const [users, setUsers] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [upsConfig, setUpsConfig] = useState(null);
  const [upsLogs, setUpsLogs] = useState([]);
  const [allLabels, setAllLabels] = useState([]);
  const [reportData, setReportData] = useState([]);
  // logoFile state removed - no longer needed
  const [loading, setLoading] = useState(false);
  
  // Static modern colors
  const BRAND_BLUE = "#3b82f6";
  const BRAND_PURPLE = "#8b5cf6";
  const [expandedGroups, setExpandedGroups] = useState({});
  const navigate = useNavigate();

  const [userDialog, setUserDialog] = useState({ open: false, mode: "create", data: null });
  const [addressDialog, setAddressDialog] = useState({ open: false, mode: "create", data: null });
  const [upsDialog, setUpsDialog] = useState(false);

  const [userForm, setUserForm] = useState({
    username: "",
    password: "",
    company_name: "",
    sender_address_id: "",
    receiver_address_id: "",
    role: "customer",
  });

  const [addressForm, setAddressForm] = useState({
    company_name: "",
    street: "",
    street2: "",
    city: "",
    postal_code: "",
    country: "DE",
    contact_name: "",
    phone: "",
    email: "",
  });

  const [upsForm, setUpsForm] = useState({
    api_key: "",
    client_id: "",
    client_secret: "",
    account_number: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, addressesRes, upsRes, labelsRes] = await Promise.all([
        axiosInstance.get("/admin/users"),
        axiosInstance.get("/admin/addresses"),
        axiosInstance.get("/admin/ups-config"),
        axiosInstance.get("/admin/labels"),
      ]);
      setUsers(usersRes.data);
      setAddresses(addressesRes.data);
      setAllLabels(labelsRes.data);
      
      if (upsRes.data && upsRes.data.exists !== false) {
        setUpsConfig(upsRes.data);
        setUpsForm({
          api_key: upsRes.data.api_key || "",
          client_id: upsRes.data.client_id || "",
          client_secret: upsRes.data.client_secret || "",
          account_number: upsRes.data.account_number || "",
        });
      }
      
      // Fetch UPS logs if config exists
      if (upsRes.data && upsRes.data.exists !== false) {
        const logsRes = await axiosInstance.get("/admin/ups-logs");
        setUpsLogs(logsRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 403) {
        toast.error("Keine Admin-Berechtigung");
        navigate("/dashboard");
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  // Design Settings Management removed - using static colors


  // Reports Management
  const fetchReports = async () => {
    try {
      const response = await axiosInstance.get("/admin/reports/labels");
      setReportData(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Reports");
    }
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/admin/reports/export-excel", {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sendungen_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Excel-Datei erfolgreich heruntergeladen!");
    } catch (error) {
      toast.error("Fehler beim Export");
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };


  // User Management
  const openUserDialog = (mode, user = null) => {
    if (mode === "edit" && user) {
      setUserForm({
        username: user.username,
        password: "",
        company_name: user.company_name,
        sender_address_id: user.sender_address_id,
        receiver_address_id: user.receiver_address_id,
        role: user.role,
      });
      setUserDialog({ open: true, mode, data: user });
    } else {
      setUserForm({
        username: "",
        password: "",
        company_name: "",
        sender_address_id: "",
        receiver_address_id: "",
        role: "customer",
      });
      setUserDialog({ open: true, mode: "create", data: null });
    }
  };

  const handleSaveUser = async () => {
    setLoading(true);
    try {
      if (userDialog.mode === "create") {
        await axiosInstance.post("/admin/users", userForm);
        toast.success("Benutzer erfolgreich erstellt!");
      } else {
        const updateData = { ...userForm };
        if (!updateData.password) {
          delete updateData.password;
        }
        await axiosInstance.put(`/admin/users/${userDialog.data.id}`, updateData);
        toast.success("Benutzer erfolgreich aktualisiert!");
      }
      setUserDialog({ open: false, mode: "create", data: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Möchten Sie diesen Benutzer wirklich löschen?")) return;
    
    try {
      await axiosInstance.delete(`/admin/users/${userId}`);
      toast.success("Benutzer gelöscht");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  // Address Management
  const openAddressDialog = (mode, address = null) => {
    if (mode === "edit" && address) {
      setAddressForm(address);
      setAddressDialog({ open: true, mode, data: address });
    } else {
      setAddressForm({
        company_name: "",
        street: "",
        street2: "",
        city: "",
        postal_code: "",
        country: "DE",
        contact_name: "",
        phone: "",
        email: "",
      });
      setAddressDialog({ open: true, mode: "create", data: null });
    }
  };

  const handleSaveAddress = async () => {
    setLoading(true);
    try {
      if (addressDialog.mode === "create") {
        await axiosInstance.post("/admin/addresses", addressForm);
        toast.success("Adresse erfolgreich erstellt!");
      } else {
        await axiosInstance.put(`/admin/addresses/${addressDialog.data.id}`, addressForm);
        toast.success("Adresse erfolgreich aktualisiert!");
      }
      setAddressDialog({ open: false, mode: "create", data: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm("Möchten Sie diese Adresse wirklich löschen?")) return;
    
    try {
      await axiosInstance.delete(`/admin/addresses/${addressId}`);
      toast.success("Adresse gelöscht");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  // UPS Config
  const handleSaveUpsConfig = async () => {
    setLoading(true);
    try {
      await axiosInstance.post("/admin/ups-config", upsForm);
      toast.success("UPS-Konfiguration gespeichert!");
      setUpsDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern der UPS-Konfiguration");
    } finally {
      setLoading(false);
    }
  };

  const handleTestUpsApi = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post("/admin/ups-config/test");
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Testen der API");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (labelId, trackingNumber) => {
    try {
      // For admin, create download link that opens in new tab
      const token = localStorage.getItem("token");
      
      // Create a blob URL using fetch with proper headers
      const response = await fetch(`${API}/admin/labels/${labelId}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        toast.error(`Fehler: Label-PDF nicht gefunden`);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab
      window.open(url, '_blank');
      
      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      
      toast.success("PDF wird geöffnet");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Fehler beim Herunterladen des PDFs");
    }
  };

  const getUPSTrackingUrl = (trackingNumber) => {
    const localeMap = {
      'de': 'de_DE',
      'en': 'en_US',
      'cs': 'cs_CZ',
      'ro': 'ro_RO'
    };
    const locale = localeMap[i18n.language] || 'en_US';
    return `https://www.ups.com/track?loc=${locale}&tracknum=${trackingNumber}`;
  };

  const handleVoidLabel = async (labelId, trackingNumber) => {
    if (!window.confirm(`Möchten Sie das Label ${trackingNumber} wirklich stornieren?`)) return;
    
    setLoading(true);
    try {
      const response = await axiosInstance.post(`/admin/labels/${labelId}/void`);
      if (response.data.ups_void_success) {
        toast.success("Label bei UPS erfolgreich storniert!");
      } else {
        toast.warning(response.data.message);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Stornieren");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDocument = async (labelId, documentIndex, fileName) => {
    try {
      const response = await fetch(`${API}/admin/labels/${labelId}/documents/${documentIndex}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab
      window.open(url, '_blank');
      
      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      
      toast.success(`${fileName} wird geöffnet`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Fehler beim Herunterladen des Dokuments");
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50" data-testid="admin-dashboard">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/4 -left-1/4 w-[500px] h-[500px] rounded-full blur-3xl" style={{ backgroundColor: `${BRAND_BLUE}08` }}></div>
        <div className="absolute -bottom-1/4 -right-1/4 w-[500px] h-[500px] rounded-full blur-3xl" style={{ backgroundColor: `${BRAND_PURPLE}08` }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 p-6 rounded-2xl shadow-lg" style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, ${BRAND_PURPLE})` }}>
          <div className="flex items-center gap-4">
            {false ? (
              <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center p-2 shadow-lg">
                <img src="" alt="Logo" className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center shadow-lg">
                <Settings className="w-7 h-7 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-white/80 text-sm mt-1">Verwaltung von Benutzern, Adressen & API</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Abmelden
          </Button>
        </div>

        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-white border border-gray-200 p-1 shadow-sm">
            <TabsTrigger
              value="users"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-gray-400 data-[state=active]:text-white text-gray-700"
              data-testid="users-tab"
            >
              <Users className="w-4 h-4 mr-2" />
              Benutzer
            </TabsTrigger>
            <TabsTrigger
              value="addresses"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-gray-400 data-[state=active]:text-white text-gray-700"
              data-testid="addresses-tab"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Adressen
            </TabsTrigger>
            <TabsTrigger
              value="labels"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-gray-400 data-[state=active]:text-white text-gray-700"
              data-testid="labels-tab"
            >
              <FileText className="w-4 h-4 mr-2" />
              Labels
            </TabsTrigger>
            <TabsTrigger
              value="ups"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-gray-400 data-[state=active]:text-white text-gray-700"
              data-testid="ups-tab"
            >
              <Package className="w-4 h-4 mr-2" />
              UPS API
            </TabsTrigger>
            <TabsTrigger
              value="logs"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-gray-400 data-[state=active]:text-white text-gray-700"
              data-testid="logs-tab"
            >
              <AlertCircle className="w-4 h-4 mr-2" />
              UPS Logs
            </TabsTrigger>
            {/* Design tab removed - using static colors */}
            <TabsTrigger
              value="reports"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-gray-400 data-[state=active]:text-white text-gray-700"
              data-testid="reports-tab"
              onClick={fetchReports}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Berichte
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <Card className="bg-white border-0 shadow-xl rounded-xl">
              <CardHeader className="border-b bg-gradient-to-r from-gray-50 to-white">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-2xl font-bold text-gray-900">Benutzer</CardTitle>
                    <CardDescription className="text-gray-600 mt-1">Verwaltung der Systembenutzer</CardDescription>
                  </div>
                  <Button onClick={() => openUserDialog("create")} data-testid="add-user-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Benutzer hinzufügen
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className="border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:border-blue-300 transition-colors"
                      data-testid={`user-item-${user.id}`}
                    >
                      <div>
                        <div className="flex items-center gap-3">
                          <p className="font-semibold text-lg">{user.company_name}</p>
                          <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                            {user.role === "admin" ? "Admin" : "Kunde"}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Username: {user.username}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openUserDialog("edit", user)}
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Addresses Tab */}
          <TabsContent value="addresses">
            <Card className="backdrop-blur-sm bg-white/95 border-0 shadow-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl">Adressverwaltung</CardTitle>
                    <CardDescription className="text-base">Absender- und Empfängeradressen verwalten</CardDescription>
                  </div>
                  <Button onClick={() => openAddressDialog("create")} data-testid="add-address-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Adresse hinzufügen
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {addresses.map((address) => (
                    <div
                      key={address.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                      data-testid={`address-item-${address.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <p className="font-semibold text-lg">{address.company_name}</p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openAddressDialog("edit", address)}
                            data-testid={`edit-address-${address.id}`}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteAddress(address.id)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-address-${address.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>{address.street}</p>
                        <p>{address.postal_code} {address.city}</p>
                        <p>{address.country}</p>
                        <p className="mt-2">{address.contact_name}</p>
                        <p>{address.phone}</p>
                        {address.email && (
                          <p className="text-blue-600">📧 {address.email}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Labels Tab */}
          <TabsContent value="labels">
            <Card className="backdrop-blur-sm bg-white/95 border-0 shadow-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl">Alle Versandlabels</CardTitle>
                    <CardDescription className="text-base">
                      Übersicht aller erstellten Labels ({allLabels.length} total)
                    </CardDescription>
                  </div>
                  <Button onClick={fetchData} variant="outline">
                    Aktualisieren
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {allLabels.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 text-lg">Noch keine Labels erstellt</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {allLabels.map((label) => (
                      <div
                        key={label.id}
                        className={`border rounded-lg p-4 ${
                          label.voided
                            ? "border-red-200 bg-red-50"
                            : "border-gray-200 bg-white hover:border-blue-300"
                        } transition-colors`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-3 flex-wrap">
                              <a 
                                href={getUPSTrackingUrl(label.tracking_number)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                              >
                                {label.tracking_number}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                              {label.voided && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                  <Ban className="w-3 h-3 mr-1" />
                                  Storniert
                                </span>
                              )}
                              <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                                {label.service_type === "express" ? "Express Saver" : "Standard"}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div>
                                <p className="text-gray-500 text-xs">Kunde</p>
                                <p className="font-medium">{label.username}</p>
                              </div>
                              <div>
                                <p className="text-gray-500 text-xs">Bestell-ID</p>
                                <p className="font-medium">{label.order_id}</p>
                              </div>
                              <div>
                                <p className="text-gray-500 text-xs">Lieferschein</p>
                                <p className="font-medium">{label.delivery_note}</p>
                              </div>
                              <div>
                                <p className="text-gray-500 text-xs">Gewicht</p>
                                <p className="font-medium">{label.weight} kg</p>
                              </div>
                            </div>
                            
                            <p className="text-xs text-gray-400">
                              Erstellt: {formatDate(label.created_at)}
                              {label.voided && ` • Storniert: ${formatDate(label.voided_at)} von ${label.voided_by}`}
                            </p>
                          </div>
                          
                          <div className="flex gap-2 ml-4 flex-wrap">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadPDF(label.id, label.tracking_number)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </Button>
                            
                            {/* Show document buttons for Switzerland shipments */}
                            {label.uploaded_documents && label.uploaded_documents.length > 0 && (
                              <div className="flex gap-1">
                                {label.uploaded_documents.map((doc, docIdx) => (
                                  <Button
                                    key={docIdx}
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownloadDocument(label.id, docIdx, doc.original_name)}
                                    className="text-green-600 hover:text-green-700"
                                    title={doc.original_name}
                                  >
                                    <FileText className="w-4 h-4 mr-1" />
                                    Dok {docIdx + 1}
                                  </Button>
                                ))}
                              </div>
                            )}
                            
                            {!label.voided && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleVoidLabel(label.id, label.tracking_number)}
                                className="text-red-600 hover:text-red-700"
                                disabled={loading}
                              >
                                <Ban className="w-4 h-4 mr-1" />
                                Stornieren
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* UPS Config Tab */}
          <TabsContent value="ups">
            <Card className="backdrop-blur-sm bg-white/95 border-0 shadow-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl">UPS API Konfiguration</CardTitle>
                    <CardDescription className="text-base">
                      {upsConfig ? "API-Zugangsdaten konfiguriert" : "Noch keine API-Daten hinterlegt"}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {upsConfig && (
                      <Button
                        onClick={handleTestUpsApi}
                        variant="outline"
                        disabled={loading}
                        data-testid="test-ups-button"
                      >
                        {loading ? "Teste..." : "API Testen"}
                      </Button>
                    )}
                    <Button onClick={() => setUpsDialog(true)} data-testid="configure-ups-button">
                      <Key className="w-4 h-4 mr-2" />
                      {upsConfig ? "Bearbeiten" : "Konfigurieren"}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {upsConfig ? (
                  <div className="space-y-4">
                    <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-600">API Key</p>
                        <p className="font-mono text-sm">••••••••{upsConfig.api_key?.slice(-4)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Client ID</p>
                        <p className="font-mono text-sm">{upsConfig.client_id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Account Number</p>
                        <p className="font-mono text-sm">{upsConfig.account_number}</p>
                      </div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800">
                        <strong>Hinweis:</strong> Klicken Sie auf "API Testen", um die Verbindung zur UPS API zu überprüfen. 
                        Der Test sendet eine OAuth Token-Anfrage an UPS. Überprüfen Sie den "UPS Logs" Tab für detaillierte Fehlerberichte.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Noch keine UPS API-Daten konfiguriert</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* UPS Logs Tab */}
          <TabsContent value="logs">
            <Card className="backdrop-blur-sm bg-white/95 border-0 shadow-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl">UPS API Logs</CardTitle>
                    <CardDescription className="text-base">
                      Letzte 50 API-Anfragen an UPS
                    </CardDescription>
                  </div>
                  <Button onClick={fetchData} variant="outline">
                    Aktualisieren
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {upsLogs.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Noch keine API-Logs vorhanden</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {upsLogs.map((log) => (
                      <div
                        key={log.id}
                        className={`border rounded-lg p-4 ${
                          log.success ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {log.success ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-600" />
                            )}
                            <div>
                              <p className="font-semibold text-sm">
                                {log.action === "oauth" && "OAuth Token Request"}
                                {log.action === "create_shipment" && "Shipment Erstellung"}
                                {log.action === "test_connection" && "Verbindungstest"}
                              </p>
                              <p className="text-xs text-gray-500">
                                {new Date(log.timestamp).toLocaleString("de-DE")}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`text-xs px-2 py-1 rounded ${
                              log.success ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                            }`}
                          >
                            {log.success ? "Erfolg" : "Fehler"}
                          </span>
                        </div>

                        {log.error_message && (
                          <div className="bg-white border border-red-300 rounded p-3 mb-3">
                            <p className="text-sm font-semibold text-red-700 mb-1">Fehlermeldung:</p>
                            <p className="text-xs text-red-600 font-mono">{log.error_message}</p>
                          </div>
                        )}

                        <details className="text-xs">
                          <summary className="cursor-pointer text-gray-700 hover:text-gray-900 font-medium">
                            Details anzeigen
                          </summary>
                          <div className="mt-2 space-y-2">
                            <div className="bg-white p-2 rounded border">
                              <p className="font-semibold mb-1">Request:</p>
                              <pre className="text-xs overflow-auto">
                                {JSON.stringify(log.request_data, null, 2)}
                              </pre>
                            </div>
                            <div className="bg-white p-2 rounded border">
                              <p className="font-semibold mb-1">Response:</p>
                              <pre className="text-xs overflow-auto">
                                {JSON.stringify(log.response_data, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </details>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Design Tab removed - using static colors */}


          {/* Reports Tab */}
          <TabsContent value="reports">
            <Card className="backdrop-blur-sm bg-white/95 border-0 shadow-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl">Label-Berichte</CardTitle>
                    <CardDescription className="text-base">
                      Übersicht aller erstellten Labels nach Kunde und Monat
                    </CardDescription>
                  </div>
                  <Button
                    onClick={handleExportExcel}
                    disabled={loading}
                    className="text-white hover:opacity-90"
                    style={{ background: `linear-gradient(to right, ${BRAND_BLUE}, ${BRAND_PURPLE})` }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Excel Export
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {reportData.length === 0 ? (
                  <div className="text-center py-12">
                    <BarChart3 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Keine Daten verfügbar</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reportData.map((group, idx) => {
                      const groupKey = `${group.company_name}-${group.month_key}`;
                      const isExpanded = expandedGroups[groupKey];
                      
                      return (
                        <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                          {/* Group Header */}
                          <div
                            className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => toggleGroup(groupKey)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {isExpanded ? (
                                  <ChevronDown className="w-5 h-5 text-gray-600" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-gray-600" />
                                )}
                                <div>
                                  <h3 className="font-semibold text-lg">{group.company_name}</h3>
                                  <p className="text-sm text-gray-600">{group.month_display}</p>
                                </div>
                              </div>
                              <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                {group.label_count} {group.label_count === 1 ? 'Label' : 'Labels'}
                              </div>
                            </div>
                          </div>

                          {/* Expanded Content */}
                          {isExpanded && (
                            <div className="p-4">
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="text-left p-2">Datum</th>
                                      <th className="text-left p-2">Tracking</th>
                                      <th className="text-left p-2">Gewicht</th>
                                      <th className="text-left p-2">Maße (L×B×H)</th>
                                      <th className="text-left p-2">Empfänger</th>
                                      <th className="text-left p-2">Ort</th>
                                      <th className="text-left p-2">Bestell-ID</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {group.labels.map((label, labelIdx) => {
                                      const date = new Date(label.created_at);
                                      const dateStr = date.toLocaleDateString('de-DE', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      });
                                      
                                      return (
                                        <tr key={labelIdx} className="border-t border-gray-200 hover:bg-gray-50">
                                          <td className="p-2">{dateStr}</td>
                                          <td className="p-2 font-mono text-xs">{label.tracking_number}</td>
                                          <td className="p-2">{label.weight} kg</td>
                                          <td className="p-2">{label.length}×{label.width}×{label.height} cm</td>
                                          <td className="p-2">{label.receiver_name}</td>
                                          <td className="p-2">{label.receiver_city}</td>
                                          <td className="p-2">{label.order_id}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


        </Tabs>
      </div>

      {/* User Dialog */}
      <Dialog open={userDialog.open} onOpenChange={(open) => setUserDialog({ ...userDialog, open })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {userDialog.mode === "create" ? "Neuer Benutzer" : "Benutzer bearbeiten"}
            </DialogTitle>
            <DialogDescription>
              {userDialog.mode === "create" ? "Erstellen Sie einen neuen Benutzer" : "Bearbeiten Sie die Benutzerdaten"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Benutzername</Label>
                <Input
                  value={userForm.username}
                  onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                  data-testid="user-username-input"
                />
              </div>
              <div>
                <Label>Passwort {userDialog.mode === "edit" && "(leer lassen für keine Änderung)"}</Label>
                <Input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  data-testid="user-password-input"
                />
              </div>
            </div>
            <div>
              <Label>Firmenname</Label>
              <Input
                value={userForm.company_name}
                onChange={(e) => setUserForm({ ...userForm, company_name: e.target.value })}
                data-testid="user-company-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Absender-Adresse</Label>
                <Select
                  value={userForm.sender_address_id}
                  onValueChange={(value) => setUserForm({ ...userForm, sender_address_id: value })}
                >
                  <SelectTrigger data-testid="user-sender-select">
                    <SelectValue placeholder="Adresse wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {addresses.map((addr) => (
                      <SelectItem key={addr.id} value={addr.id}>
                        {addr.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Empfänger-Adresse</Label>
                <Select
                  value={userForm.receiver_address_id}
                  onValueChange={(value) => setUserForm({ ...userForm, receiver_address_id: value })}
                >
                  <SelectTrigger data-testid="user-receiver-select">
                    <SelectValue placeholder="Adresse wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {addresses.map((addr) => (
                      <SelectItem key={addr.id} value={addr.id}>
                        {addr.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Rolle</Label>
              <Select value={userForm.role} onValueChange={(value) => setUserForm({ ...userForm, role: value })}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Kunde</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialog({ ...userDialog, open: false })}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveUser} disabled={loading} data-testid="save-user-button">
              {loading ? "Wird gespeichert..." : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Address Dialog */}
      <Dialog open={addressDialog.open} onOpenChange={(open) => setAddressDialog({ ...addressDialog, open })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {addressDialog.mode === "create" ? "Neue Adresse" : "Adresse bearbeiten"}
            </DialogTitle>
            <DialogDescription>
              {addressDialog.mode === "create" ? "Erstellen Sie eine neue Adresse" : "Bearbeiten Sie die Adressdaten"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Firmenname</Label>
              <Input
                value={addressForm.company_name}
                onChange={(e) => setAddressForm({ ...addressForm, company_name: e.target.value })}
                data-testid="address-company-input"
              />
            </div>
            <div>
              <Label>Straße</Label>
              <Input
                value={addressForm.street}
                onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })}
                data-testid="address-street-input"
                placeholder="Hauptstraße 123"
              />
            </div>
            <div>
              <Label>Straße 2 (optional)</Label>
              <Input
                value={addressForm.street2 || ''}
                onChange={(e) => setAddressForm({ ...addressForm, street2: e.target.value })}
                data-testid="address-street2-input"
                placeholder="Gebäude B, 2. Stock"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>PLZ</Label>
                <Input
                  value={addressForm.postal_code}
                  onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })}
                  data-testid="address-postal-input"
                />
              </div>
              <div>
                <Label>Stadt</Label>
                <Input
                  value={addressForm.city}
                  onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                  data-testid="address-city-input"
                />
              </div>
            </div>
            <div>
              <Label>Land</Label>
              <Select
                value={addressForm.country}
                onValueChange={(value) => setAddressForm({ ...addressForm, country: value })}
              >
                <SelectTrigger data-testid="address-country-select">
                  <SelectValue placeholder="Land auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DE">🇩🇪 Deutschland</SelectItem>
                  <SelectItem value="AT">🇦🇹 Österreich</SelectItem>
                  <SelectItem value="CH">🇨🇭 Schweiz</SelectItem>
                  <SelectItem value="RO">🇷🇴 Rumänien</SelectItem>
                  <SelectItem value="BG">🇧🇬 Bulgarien</SelectItem>
                  <SelectItem value="CZ">🇨🇿 Tschechien</SelectItem>
                  <SelectItem value="PL">🇵🇱 Polen</SelectItem>
                  <SelectItem value="HU">🇭🇺 Ungarn</SelectItem>
                  <SelectItem value="SK">🇸🇰 Slowakei</SelectItem>
                  <SelectItem value="SI">🇸🇮 Slowenien</SelectItem>
                  <SelectItem value="HR">🇭🇷 Kroatien</SelectItem>
                  <SelectItem value="IT">🇮🇹 Italien</SelectItem>
                  <SelectItem value="FR">🇫🇷 Frankreich</SelectItem>
                  <SelectItem value="ES">🇪🇸 Spanien</SelectItem>
                  <SelectItem value="NL">🇳🇱 Niederlande</SelectItem>
                  <SelectItem value="BE">🇧🇪 Belgien</SelectItem>
                  <SelectItem value="LU">🇱🇺 Luxemburg</SelectItem>
                  <SelectItem value="DK">🇩🇰 Dänemark</SelectItem>
                  <SelectItem value="SE">🇸🇪 Schweden</SelectItem>
                  <SelectItem value="NO">🇳🇴 Norwegen</SelectItem>
                  <SelectItem value="FI">🇫🇮 Finnland</SelectItem>
                  <SelectItem value="GB">🇬🇧 Großbritannien</SelectItem>
                  <SelectItem value="IE">🇮🇪 Irland</SelectItem>
                  <SelectItem value="PT">🇵🇹 Portugal</SelectItem>
                  <SelectItem value="GR">🇬🇷 Griechenland</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ansprechpartner</Label>
                <Input
                  value={addressForm.contact_name}
                  onChange={(e) => setAddressForm({ ...addressForm, contact_name: e.target.value })}
                  data-testid="address-contact-input"
                />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input
                  value={addressForm.phone}
                  onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })}
                  data-testid="address-phone-input"
                />
              </div>
            </div>
            <div>
              <Label>E-Mail (für UPS Quantum View Benachrichtigungen)</Label>
              <Input
                type="email"
                value={addressForm.email}
                onChange={(e) => setAddressForm({ ...addressForm, email: e.target.value })}
                placeholder="email@beispiel.de"
                data-testid="address-email-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional: Empfänger erhält automatisch Versandbenachrichtigungen von UPS
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddressDialog({ ...addressDialog, open: false })}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveAddress} disabled={loading} data-testid="save-address-button">
              {loading ? "Wird gespeichert..." : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UPS Config Dialog */}
      <Dialog open={upsDialog} onOpenChange={setUpsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>UPS API Konfiguration</DialogTitle>
            <DialogDescription>Geben Sie Ihre UPS API-Zugangsdaten ein</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>API Key</Label>
              <Input
                value={upsForm.api_key}
                onChange={(e) => setUpsForm({ ...upsForm, api_key: e.target.value })}
                data-testid="ups-api-key-input"
              />
            </div>
            <div>
              <Label>Client ID</Label>
              <Input
                value={upsForm.client_id}
                onChange={(e) => setUpsForm({ ...upsForm, client_id: e.target.value })}
                data-testid="ups-client-id-input"
              />
            </div>
            <div>
              <Label>Client Secret</Label>
              <Input
                type="password"
                value={upsForm.client_secret}
                onChange={(e) => setUpsForm({ ...upsForm, client_secret: e.target.value })}
                data-testid="ups-client-secret-input"
              />
            </div>
            <div>
              <Label>Account Number</Label>
              <Input
                value={upsForm.account_number}
                onChange={(e) => setUpsForm({ ...upsForm, account_number: e.target.value })}
                data-testid="ups-account-number-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpsDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveUpsConfig} disabled={loading} data-testid="save-ups-config-button">
              {loading ? "Wird gespeichert..." : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Footer */}
      <footer className="mt-12 py-6 text-center text-sm text-gray-600 border-t">
        <div className="space-y-2">
          <p>© 2025 - Kauz Logistik e.K. - Industriestr. 18 - DE-63834 Sulzbach</p>
          <p><a href="mailto:mbe0020@mbe.de" className="text-blue-600 hover:underline">mbe0020@mbe.de</a></p>
        </div>
      </footer>
    </div>
  );
}
